/**
 * MediaLibraryBrowser Component
 *
 * Modal for browsing and selecting images from the media library.
 * Features:
 * - Grid view of all media items
 * - Search by title/filename
 * - Filter by tags
 * - Toggle visibility filter
 * - Quick upload within browser
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  Upload,
  Loader2,
  Image as ImageIcon,
  Check,
  Filter,
  RefreshCw,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { media, uploads } from '@/services/api/admin';

/**
 * MediaLibraryBrowser - Modal for browsing media library
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Close callback
 * @param {Function} props.onSelect - Selection callback with media item
 * @param {string} [props.context] - Context for new uploads
 */
export function MediaLibraryBrowser({
  open,
  onClose,
  onSelect,
  context = null,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showHidden, setShowHidden] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const fileInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const PAGE_SIZE = 24;

  // Fetch media items
  const fetchMedia = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page,
        pageSize: PAGE_SIZE,
        sortBy: 'created_at',
        sortOrder: 'desc',
      };

      if (search) params.search = search;
      if (selectedTags.length > 0) params.tags = selectedTags.join(',');
      if (!showHidden) params.showInGallery = true;

      const result = await media.list(params);
      setItems(result.items);
      setTotalPages(result.total_pages);
      setTotal(result.total);
    } catch (err) {
      setError(err.message || 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedTags, showHidden]);

  // Fetch all tags
  const fetchTags = useCallback(async () => {
    try {
      const tags = await media.getTags();
      setAllTags(tags);
    } catch (err) {
      console.error('Failed to load tags:', err);
    }
  }, []);

  // Load data when modal opens
  useEffect(() => {
    if (open) {
      fetchMedia();
      fetchTags();
    }
  }, [open, fetchMedia, fetchTags]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchMedia();
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  // Refetch when filters change
  useEffect(() => {
    if (open) {
      setPage(1);
      fetchMedia();
    }
  }, [selectedTags, showHidden]);

  // Handle file upload
  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const result = await uploads.uploadImage(file, {
        autoGallery: true,
        context,
      });

      // Refresh the list to show new upload
      await fetchMedia();

      // Auto-select the newly uploaded item
      if (result.gallery_item_id) {
        const newItem = items.find(i => i.id === result.gallery_item_id);
        if (newItem) {
          setSelectedItem(newItem);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle item click
  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  // Handle item double-click to select
  const handleItemDoubleClick = (item) => {
    onSelect(item);
    onClose();
  };

  // Handle confirm selection
  const handleConfirm = () => {
    if (selectedItem) {
      onSelect(selectedItem);
      onClose();
    }
  };

  // Toggle tag filter
  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Media Library
            {total > 0 && (
              <span className="text-sm font-normal text-stone-500">
                ({total} items)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Search and Controls */}
        <div className="flex flex-col gap-3 py-2 border-b">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title or filename..."
                className="pl-10"
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-stone-100' : ''}
            >
              <Filter className="h-4 w-4 mr-1.5" />
              Filters
              {selectedTags.length > 0 && (
                <Badge variant="secondary" className="ml-1.5">
                  {selectedTags.length}
                </Badge>
              )}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-1.5" />
                  Upload
                </>
              )}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={fetchMedia}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="flex flex-wrap items-center gap-4 p-3 bg-stone-50 rounded-lg">
              {/* Show hidden toggle */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="showHidden"
                  checked={showHidden}
                  onCheckedChange={setShowHidden}
                />
                <Label htmlFor="showHidden" className="text-sm cursor-pointer">
                  Include hidden
                </Label>
              </div>

              {/* Tags */}
              {allTags.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-stone-500">Tags:</span>
                  {allTags.slice(0, 10).map(({ tag, count }) => (
                    <Badge
                      key={tag}
                      variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleTag(tag)}
                    >
                      {tag} ({count})
                    </Badge>
                  ))}
                </div>
              )}

              {selectedTags.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedTags([])}
                  className="text-xs"
                >
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}

        {/* Media Grid */}
        <div className="flex-1 overflow-y-auto min-h-[300px]">
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-stone-500">
              <ImageIcon className="h-12 w-12 mb-2 text-stone-300" />
              <p>No images found</p>
              <p className="text-sm">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2 p-2">
              {items.map((item) => (
                <div
                  key={item.id}
                  className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-all group
                    ${selectedItem?.id === item.id
                      ? 'border-amber-500 ring-2 ring-amber-200'
                      : 'border-transparent hover:border-stone-300'
                    }
                    ${!item.show_in_gallery ? 'opacity-75' : ''}
                  `}
                  onClick={() => handleItemClick(item)}
                  onDoubleClick={() => handleItemDoubleClick(item)}
                >
                  <img
                    src={item.media_url}
                    alt={item.title}
                    className="w-full h-full object-cover bg-stone-100"
                    loading="lazy"
                  />

                  {/* Selection indicator */}
                  {selectedItem?.id === item.id && (
                    <div className="absolute top-1 right-1 bg-amber-500 text-white rounded-full p-0.5">
                      <Check className="h-3 w-3" />
                    </div>
                  )}

                  {/* Hidden indicator */}
                  {!item.show_in_gallery && (
                    <div className="absolute top-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                      Hidden
                    </div>
                  )}

                  {/* Hover overlay with info */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs truncate">{item.title}</p>
                    {item.file_size_bytes && (
                      <p className="text-white/70 text-xs">
                        {formatFileSize(item.file_size_bytes)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 py-2 border-t">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-stone-500">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        )}

        {/* Selected item info & Actions */}
        <div className="flex items-center justify-between pt-3 border-t">
          {selectedItem ? (
            <div className="flex items-center gap-3">
              <img
                src={selectedItem.media_url}
                alt={selectedItem.title}
                className="h-10 w-14 object-cover rounded"
              />
              <div>
                <p className="text-sm font-medium">{selectedItem.title}</p>
                {selectedItem.tags?.length > 0 && (
                  <div className="flex gap-1 mt-0.5">
                    {selectedItem.tags.slice(0, 3).map(tag => (
                      <Badge key={tag} variant="outline" className="text-xs py-0">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-stone-500">
              Click to select, double-click to confirm
            </p>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedItem}
            >
              Select Image
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MediaLibraryBrowser;
